/**
 * A table's state in the address bar: search, page, page size, sort and tab.
 *
 * The rule the whole console follows is that a link reproduces the screen and
 * Back undoes the last thing a person did that changed what they were looking
 * at. For a table that means:
 *
 * - `q`, `page` (one-based), `size`, `sort`, `order` and `tab` are params.
 * - A tab is a place, so changing it PUSHES a history entry. Search, sort, size
 *   and filters are adjustments, so they REPLACE: typing a word in the search
 *   must not leave one history entry per keystroke.
 * - Anything that changes which rows match sends the page back to the first.
 *   Page three of the old answer is a meaningless offset into the new one.
 * - A value equal to its default is left out, so an untouched list has a clean
 *   URL and two links to the same view are the same string.
 *
 * Pure functions over `URLSearchParams`, so all of it is tested without a
 * router. `useTableUrlState` is the thin hook that wires them to one.
 */
import { DEFAULT_PAGE_SIZE, PAGE_SIZES } from './pagination-model'

export type SortOrder = 'asc' | 'desc'

export interface TableSort {
  key: string
  order: SortOrder
}

export interface TableUrlOptions {
  /** The sizes the footer offers. A `size` param outside them is ignored. */
  pageSizes?: readonly number[]
  defaultPageSize?: number
  /** The column keys a person may sort by. A `sort` param naming another is ignored. */
  sortKeys?: readonly string[]
  defaultSort?: TableSort
  /** The tab ids. A `tab` param naming another falls back to `defaultTab`. */
  tabs?: readonly string[]
  defaultTab?: string
}

export interface TableUrlState {
  q: string
  /** Zero-based, the way the pager counts. The address bar is one-based. */
  pageIndex: number
  pageSize: number
  sort: TableSort | undefined
  tab: string | undefined
}

export interface TableUrlPatch {
  q?: string
  pageIndex?: number
  pageSize?: number
  /** `null` clears the sort back to the default. */
  sort?: TableSort | null
  tab?: string
}

function isOrder(text: string | null): text is SortOrder {
  return text === 'asc' || text === 'desc'
}

/** A positive whole number, or nothing. `page=2abc` is not page two. */
function wholeNumber(text: string | null): number | undefined {
  if (text === null || !/^\d+$/.test(text)) return undefined
  const value = Number(text)
  return Number.isSafeInteger(value) && value > 0 ? value : undefined
}

export function readSort(params: URLSearchParams, options: TableUrlOptions = {}): TableSort | undefined {
  const key = params.get('sort')
  if (key === null || key === '') return options.defaultSort
  if (options.sortKeys !== undefined && !options.sortKeys.includes(key)) return options.defaultSort
  const order = params.get('order')
  return { key, order: isOrder(order) ? order : 'asc' }
}

export function readTableUrl(params: URLSearchParams, options: TableUrlOptions = {}): TableUrlState {
  const sizes = options.pageSizes ?? PAGE_SIZES
  const fallbackSize = options.defaultPageSize ?? DEFAULT_PAGE_SIZE
  const size = wholeNumber(params.get('size'))
  const page = wholeNumber(params.get('page'))
  const tab = params.get('tab')
  return {
    q: params.get('q') ?? '',
    pageIndex: page === undefined ? 0 : page - 1,
    pageSize: size !== undefined && sizes.includes(size) ? size : fallbackSize,
    sort: readSort(params, options),
    tab:
      tab !== null && (options.tabs === undefined || options.tabs.includes(tab)) ? tab : options.defaultTab,
  }
}

/**
 * The address bar after a change, and whether it replaces the current entry.
 *
 * Everything not named in the patch is left exactly as it was, including params
 * that have nothing to do with the table, such as an open drawer.
 */
export function writeTableUrl(
  params: URLSearchParams,
  patch: TableUrlPatch,
  options: TableUrlOptions = {},
): { params: URLSearchParams; replace: boolean } {
  const next = new URLSearchParams(params)
  const fallbackSize = options.defaultPageSize ?? DEFAULT_PAGE_SIZE
  let resetPage = false

  if (patch.q !== undefined) {
    const q = patch.q
    if (q.trim() === '') next.delete('q')
    else next.set('q', q)
    resetPage = true
  }

  if (patch.pageSize !== undefined) {
    if (patch.pageSize === fallbackSize) next.delete('size')
    else next.set('size', String(patch.pageSize))
    resetPage = true
  }

  if (patch.sort !== undefined) {
    const sort = patch.sort
    const isDefault =
      sort === null ||
      (options.defaultSort !== undefined &&
        sort.key === options.defaultSort.key &&
        sort.order === options.defaultSort.order)
    if (isDefault || sort === null) {
      next.delete('sort')
      next.delete('order')
    } else {
      next.set('sort', sort.key)
      next.set('order', sort.order)
    }
    resetPage = true
  }

  if (patch.tab !== undefined) {
    if (patch.tab === options.defaultTab) next.delete('tab')
    else next.set('tab', patch.tab)
    resetPage = true
  }

  if (patch.pageIndex !== undefined && !resetPage) {
    if (patch.pageIndex <= 0) next.delete('page')
    else next.set('page', String(Math.floor(patch.pageIndex) + 1))
  } else if (resetPage) {
    next.delete('page')
  }

  return { params: next, replace: patch.tab === undefined }
}

/**
 * What a click on a sortable header asks for.
 *
 * A new column starts ascending, and the same column flips. There is no third
 * "unsorted" click: a list always has some order, and a header that cycles
 * through a state nobody can see is a header that seems to ignore every third
 * click.
 */
export function nextHeaderSort(current: TableSort | undefined, key: string): TableSort {
  if (current === undefined || current.key !== key) return { key, order: 'asc' }
  return { key, order: current.order === 'asc' ? 'desc' : 'asc' }
}

/** The `aria-sort` a header carries, so a screen reader hears the order too. */
export function ariaSort(current: TableSort | undefined, key: string): 'ascending' | 'descending' | 'none' {
  if (current === undefined || current.key !== key) return 'none'
  return current.order === 'asc' ? 'ascending' : 'descending'
}
